import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Instagram, Facebook, User, Sparkles, PhoneCall, ChevronRight, Menu } from 'lucide-react';
import { useLang } from '@/lib/LangContext';
import { LANGS } from '@/lib/i18n';
import { TID } from '@/constants/testIds';
import { useAuth } from '@/cabinet/context/AuthContext';
import ConsultationDialog from '@/components/ConsultationDialog';

const LOGO = 'https://customer-assets.emergentagent.com/job_smart-feed-pets/artifacts/ygio5kkm_1Smartpaw%20Post%20-%2035%20copy.PNG';

const SOCIALS = [
  { name: 'Instagram', href: 'https://www.instagram.com/smartpaw__/', icon: Instagram },
  { name: 'Facebook', href: 'https://www.facebook.com/yoursmartpaw', icon: Facebook },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@smartpaw__?is_from_webapp=1&sender_device=pc',
    icon: (props) => (
      <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M16.5 3a5.5 5.5 0 0 0 4.5 4.5v3a8.5 8.5 0 0 1-4.5-1.3v6.6a6.2 6.2 0 1 1-6.2-6.2c.35 0 .69.03 1.02.09v3.1a3.2 3.2 0 1 0 2.18 3.02V3h3z" />
      </svg>
    ),
  },
];

// Smooth-scroll to in-page section. Falls back to router nav for cross-page.
function scrollToId(id) {
  if (window.location.pathname !== '/') {
    window.location.href = `/#${id}`;
    return;
  }
  const el = document.getElementById(id);
  if (el) {
    const offset = 90; // compensate for sticky header
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
    history.replaceState(null, '', `#${id}`);
  }
}

export default function Header({ onOpenSignup }) {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [consultOpen, setConsultOpen] = useState(false);

  const firstName = (user?.name || user?.email || '').trim().split(/\s+/)[0] || '';
  const initials = (() => {
    const src = (user?.name || user?.email || '').trim();
    if (!src) return '';
    const parts = src.split(/[\s@]+/).filter(Boolean);
    return (parts[0]?.[0] || '').concat(parts[1]?.[0] || '').toUpperCase();
  })();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the menu sheet is open.
  useEffect(() => {
    document.documentElement.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.documentElement.style.overflow = ''; };
  }, [menuOpen]);

  const simple = (t.nav && t.nav.simple) || {};
  const navItems = [
    { kind: 'route', to: '/', label: simple.home || 'Home', tid: 'nav-home-link' },
    { kind: 'route', to: '/about', label: simple.aboutUs, tid: 'nav-about-link' },
    { kind: 'anchor', id: 'how', label: simple.how, tid: 'nav-how-link' },
    { kind: 'anchor', id: 'partners', label: simple.brands, tid: 'nav-brands-link' },
    { kind: 'anchor', id: 'feeder', label: simple.feeder, tid: 'nav-feeder-link', highlight: true },
    { kind: 'anchor', id: 'experts', label: simple.experts, tid: 'nav-experts-link' },
    { kind: 'anchor', id: 'contact', label: simple.contact, tid: 'nav-contact-link' },
  ];

  const closeMenu = () => setMenuOpen(false);
  const handleNav = (it) => {
    closeMenu();
    if (it.kind === 'anchor') scrollToId(it.id);
    else if (it.to) window.location.assign(it.to);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
          scrolled
            ? 'bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-[#0A4D8C1A]'
            : 'bg-transparent'
        }`}
      >
        {/*
          Header shows the logo (home link) on the left and a slim utility
          cluster on the right: EN/KA toggle, social icons, primary Register
          CTA, and a menu button that opens the full navigation sheet.
          See amendment brief PO1 \u2014 we still want the first screen to feel
          calm; the full nav lives behind the menu button.
        */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-20 md:h-24 flex items-center justify-between gap-3">
          <Link
            data-testid={TID.header.logo}
            to="/"
            aria-label="SmartPaw Food — home"
            className="group inline-flex items-center gap-2.5 shrink-0"
          >
            <img
              src={LOGO}
              alt="SmartPaw Food"
              className="h-11 w-11 md:h-14 md:w-14 object-contain transition-transform duration-300 group-hover:rotate-[-6deg]"
            />
            <span className="font-display font-extrabold text-[#0A4D8C] text-base md:text-lg lg:text-xl tracking-tight hidden sm:block leading-none">
              SmartPaw <span className="text-[#F25C05]">Food</span>
            </span>
          </Link>

          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* Language toggle */}
            <div
              data-testid={TID.header.langToggle}
              className="hidden sm:flex items-center bg-white border border-[#0A4D8C1A] rounded-full p-0.5"
              role="group"
              aria-label="Language toggle"
            >
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  data-testid={`lang-${l.code}-button`}
                  className={`text-[11px] md:text-xs font-bold px-2.5 md:px-3 py-1 md:py-1.5 rounded-full transition-all ${
                    lang === l.code
                      ? 'bg-[#0A4D8C] text-white'
                      : 'text-[#465B70] hover:text-[#0A4D8C]'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* Social icons — small, round, hidden on narrow phones */}
            <div className="hidden md:flex items-center gap-1.5">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    data-testid={`header-social-${s.name.toLowerCase()}`}
                    className="w-9 h-9 rounded-full border border-[#0A4D8C1A] bg-white/80 text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C] hover:text-white hover:border-[#0A4D8C] transition-all"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>

            {user && (
              <Link
                to="/cabinet/dashboard"
                data-testid="header-user-link"
                aria-label={firstName ? `Open cabinet · ${firstName}` : 'Open cabinet'}
                className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#0A4D8C] text-white px-2.5 py-1.5 hover:bg-[#063a6e] transition-colors"
              >
                <span className="w-7 h-7 shrink-0 rounded-full bg-white text-[#0A4D8C] flex items-center justify-center text-[11px] font-extrabold">
                  {initials || <User size={14} />}
                </span>
              </Link>
            )}

            {/* Schedule Free Consultation — secondary CTA, sits BEFORE Register */}
            <button
              type="button"
              data-testid="header-consult-cta"
              onClick={() => setConsultOpen(true)}
              className="hidden lg:inline-flex items-center gap-1.5 rounded-full border-2 border-[#0A4D8C] text-[#0A4D8C] px-3 xl:px-4 py-2 text-[11px] xl:text-[12px] font-bold hover:bg-[#0A4D8C] hover:text-white transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4D8C]/40"
            >
              <PhoneCall size={13} className="shrink-0" />
              {t.nav.consult}
            </button>

            {/* Register CTA — always visible */}
            <button
              data-testid={TID.header.cta}
              onClick={onOpenSignup}
              className="hidden sm:inline-flex btn-primary !text-[12px] md:!text-[13px] !px-3.5 md:!px-5 !py-2 md:!py-2.5 whitespace-nowrap"
            >
              {t.nav.cta}
            </button>

            {/* Menu button — opens the full nav sheet */}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label={simple.menu || 'Open menu'}
              data-testid="header-menu-trigger"
              className="w-10 h-10 md:w-11 md:h-11 rounded-full border border-[#0A4D8C33] bg-white/80 text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C] hover:text-white hover:border-[#0A4D8C] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4D8C]/40"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Slide-over menu sheet */}
      {menuOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="header-menu-sheet">
          <div
            className="absolute inset-0 bg-[#05223D]/50 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={closeMenu}
          />
          <aside className="relative ml-auto h-full w-full sm:max-w-md bg-[#FDFBF7] shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-5 md:px-7 h-20 border-b border-[#0A4D8C1A]">
              <Link to="/" onClick={closeMenu} className="flex items-center gap-2.5" data-testid="menu-sheet-logo">
                <img src={LOGO} alt="SmartPaw Food" className="h-10 w-10 object-contain" />
                <span className="font-display font-extrabold text-[#0A4D8C] text-lg leading-none">
                  SmartPaw <span className="text-[#F25C05]">Food</span>
                </span>
              </Link>
              <button
                type="button"
                onClick={closeMenu}
                data-testid="menu-sheet-close"
                aria-label={simple.closeMenu || 'Close menu'}
                className="w-10 h-10 rounded-full border border-[#0A4D8C33] flex items-center justify-center text-[#0A4D8C] hover:bg-[#0A4D8C] hover:text-white hover:border-[#0A4D8C] transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 md:px-7 py-6">
              {/* Language toggle */}
              <div
                data-testid={TID.header.langToggle}
                className="inline-flex items-center bg-white border border-[#0A4D8C1A] rounded-full p-1 mb-6"
                role="group"
                aria-label="Language toggle"
              >
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    data-testid={`lang-${l.code}-button`}
                    className={`text-xs font-bold px-4 py-1.5 rounded-full transition-all ${
                      lang === l.code ? 'bg-[#0A4D8C] text-white' : 'text-[#465B70] hover:text-[#0A4D8C]'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              <nav className="flex flex-col gap-1">
                {navItems.map((it) => {
                  const classes = it.highlight
                    ? 'inline-flex items-center justify-between gap-2 px-4 py-3.5 rounded-2xl text-white font-extrabold bg-gradient-to-r from-[#F25C05] to-[#D44D04] shadow-[0_6px_18px_rgba(242,92,5,0.3)]'
                    : 'flex items-center justify-between gap-2 px-4 py-3.5 rounded-2xl font-semibold text-[#05223D] hover:bg-[#F5F2EB]';
                  if (it.kind === 'route') {
                    return (
                      <Link
                        key={it.to}
                        to={it.to}
                        onClick={closeMenu}
                        data-testid={it.tid}
                        className={classes}
                      >
                        <span className="inline-flex items-center gap-2">
                          {it.highlight && <Sparkles size={16} />}
                          {it.label}
                        </span>
                        <ChevronRight size={16} className="opacity-60" />
                      </Link>
                    );
                  }
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => handleNav(it)}
                      data-testid={it.tid}
                      className={classes}
                    >
                      <span className="inline-flex items-center gap-2">
                        {it.highlight && <Sparkles size={16} />}
                        {it.label}
                      </span>
                      <ChevronRight size={16} className="opacity-60" />
                    </button>
                  );
                })}
              </nav>

              <div className="mt-6 grid gap-3">
                <button
                  onClick={() => { onOpenSignup(); closeMenu(); }}
                  className="btn-primary justify-center"
                  data-testid={TID.header.cta}
                >
                  {t.nav.cta}
                </button>
                <button
                  type="button"
                  onClick={() => { setConsultOpen(true); closeMenu(); }}
                  data-testid="header-consult-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0A4D8C] text-[#0A4D8C] py-3 px-5 font-bold hover:bg-[#0A4D8C] hover:text-white transition-colors"
                >
                  <PhoneCall size={16} />
                  {t.nav.consult}
                </button>
                {user && (
                  <Link
                    to="/cabinet/dashboard"
                    onClick={closeMenu}
                    data-testid="header-user-link"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A4D8C] text-white py-2.5 px-4 hover:bg-[#063a6e]"
                  >
                    <span className="w-6 h-6 rounded-full bg-white text-[#0A4D8C] flex items-center justify-center text-[10px] font-extrabold">
                      {initials || <User size={12} />}
                    </span>
                    <span className="text-sm font-bold">{firstName}</span>
                  </Link>
                )}
              </div>
            </div>

            <div className="border-t border-[#0A4D8C1A] px-5 md:px-7 py-5 flex items-center justify-center gap-3">
              {SOCIALS.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.name}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                    className="w-10 h-10 rounded-full border border-[#0A4D8C1A] text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C] hover:text-white hover:border-[#0A4D8C] transition-all"
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </aside>
        </div>
      )}

      <ConsultationDialog open={consultOpen} onOpenChange={setConsultOpen} />
    </>
  );
}
